import { cx } from '@/components/ui/classNames';
import { resolveIconClass } from '@/components/ui/icons';
import { formatNumber } from '@/utils/number';

export interface GridPagerProps {
  /** 1-basiert. */
  page: number;
  pageSize: number;
  pageSizes: readonly number[];
  totalCount: number;
  /** Höchstzahl sichtbarer Seitenzahlen (`PageCount`). */
  pageCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

interface NavProps {
  className: string;
  icon: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
}

function Nav(props: NavProps) {
  return (
    <div
      role="button"
      aria-label={props.label}
      aria-disabled={props.disabled}
      className={cx(
        props.disabled ? `${props.className}disabled` : props.className,
        'e-icons',
        resolveIconClass(props.icon),
      )}
      onClick={() => {
        if (!props.disabled) props.onClick();
      }}
    />
  );
}

/** Sichtbarer Ausschnitt der Seitenzahlen; die aktuelle Seite liegt möglichst mittig. */
function pageWindow(page: number, pages: number, size: number): number[] {
  const start = Math.max(1, Math.min(page - Math.floor(size / 2), pages - size + 1));
  const length = Math.min(size, pages);
  return Array.from({ length }, (_unused, index) => start + index);
}

export function GridPager(props: GridPagerProps) {
  const { onPageChange, page, pageSize, totalCount } = props;
  const pages = Math.max(1, Math.ceil(totalCount / pageSize));
  const current = Math.min(page, pages);
  const from = totalCount === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, totalCount);

  return (
    <div className="e-pager">
      <div className="e-pagercontainer">
        <Nav
          className="e-firstpage"
          icon="e-first-page"
          label="Erste Seite"
          disabled={current <= 1}
          onClick={() => {
            onPageChange(1);
          }}
        />
        <Nav
          className="e-prevpage"
          icon="e-prev-page"
          label="Vorherige Seite"
          disabled={current <= 1}
          onClick={() => {
            onPageChange(current - 1);
          }}
        />
        {pageWindow(current, pages, props.pageCount).map((number) => (
          <a
            key={number}
            aria-current={number === current}
            className={cx(
              'e-numericitem',
              'e-spacing',
              // Seitenzahlen stehen im Entwurf in der Monospace-Familie (REQ-21 §6).
              'iwm-num',
              number === current && 'e-currentitem e-active',
            )}
            onClick={() => {
              onPageChange(number);
            }}
          >
            {number}
          </a>
        ))}
        <Nav
          className="e-nextpage"
          icon="e-next-page"
          label="Nächste Seite"
          disabled={current >= pages}
          onClick={() => {
            onPageChange(current + 1);
          }}
        />
        <Nav
          className="e-lastpage"
          icon="e-last-page"
          label="Letzte Seite"
          disabled={current >= pages}
          onClick={() => {
            onPageChange(pages);
          }}
        />
      </div>
      <div className="e-pagesizes">
        {/* Der Entwurf setzt hier eine einfache Auswahlliste mit vorangestellter
            Beschriftung (REQ-21 §6); das umschliessende <label> ist zugleich ihr Name. */}
        <label className="iwm-pager-rows">
          Zeilen
          <select
            className="iwm-pager-select iwm-num"
            value={pageSize}
            onChange={(event) => {
              props.onPageSizeChange(Number(event.target.value));
            }}
          >
            {props.pageSizes.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="e-parentmsgbar">
        <span className="e-pagermessage">
          <span className="iwm-num">
            {formatNumber(from, 'N0')}–{formatNumber(to, 'N0')}
          </span>{' '}
          von <span className="iwm-num">{formatNumber(totalCount, 'N0')}</span> Einträgen
        </span>
      </div>
    </div>
  );
}

export default GridPager;
